/**
 * The one status -> tone map. Five tones (DESIGN.md "Status"), rendered by
 * <lb-status-badge> as a soft pill with a dot:
 *   plain  neutral, not started or no longer relevant (draft, cancelled, closed)
 *   info   waiting on us / accepted (submitted, approved, open)
 *   warn   in progress, or needs attention soon (in production, ordered, due soon)
 *   ok     done (ready, delivered, received, paid)
 *   bad    a problem (declined, failed, overdue)
 * Statuses that share a tone are told apart by their word, which is always shown.
 */
export type StatusKind = 'order' | 'shipment' | 'purchaseOrder' | 'purchaseRequest' | 'stockCount' | 'productionOrder' | 'kpi' | 'damage' | 'billing';
export type StatusTone = 'plain' | 'info' | 'warn' | 'ok' | 'bad';

const TONES: { [kind in StatusKind]: { [status: string]: StatusTone } } = {
  order: {
    'New': 'plain',
    'Submitted': 'info',
    'Approved': 'info',
    'Approved (Modified)': 'info',
    'Declined': 'bad',
    'In Production': 'warn',
    'Ready for Shipment': 'ok',
    'Partially Shipped': 'warn',
    'Shipped': 'ok',
    'Delivered': 'ok',
    'Cancelled': 'plain',
    'Closed': 'plain'
  },
  shipment: {
    'Pending': 'info',
    'Assigned': 'info',
    'In Transit': 'warn',
    'Delivered': 'ok',
    'Failed': 'bad',
    'Returned': 'bad',
    'Cancelled': 'plain'
  },
  purchaseOrder: {
    'Draft': 'plain',
    'Submitted': 'info',
    'Ordered': 'warn',
    'Partially Received': 'warn',
    'Received': 'ok',
    'Closed': 'plain',
    'Declined': 'bad',
    'Cancelled': 'plain'
  },
  purchaseRequest: {
    'Open': 'info',
    'Processed': 'ok',
    'Cancelled': 'plain'
  },
  stockCount: {
    'Counting': 'warn',
    'Recount': 'warn',
    'Submitted': 'info',
    'Posted': 'ok',
    'Cancelled': 'plain'
  },
  productionOrder: {
    'Pending': 'info',
    'InProduction': 'warn',
    'Complete': 'ok',
    'Cancelled': 'plain'
  },
  kpi: {
    'Pending': 'plain',
    'DueSoon': 'warn',
    'Overdue': 'bad',
    'Missed': 'bad',
    'Done': 'ok',
    'DoneLate': 'warn',
    'NotApplicable': 'plain'
  },
  damage: {
    'Return': 'warn',
    'Replace': 'info',
    'Credit': 'plain',
    'Accept': 'ok'
  },
  billing: {
    'Pending': 'warn',
    'Billed': 'info',
    'Paid': 'ok',
    'Exported': 'plain',
    // Order billing on the finance page (the API's values, upper case): waiting on the
    // office to bill, billed (done), or changed after billing and needing a re-bill
    'NOT BILLED': 'info',
    'BILLED': 'ok',
    'CHANGE NOT BILLED': 'warn'
  }
};

const LABELS: { [status: string]: string } = {
  InProduction: 'In production',
  DueSoon: 'Due soon',
  DoneLate: 'Done late',
  NotApplicable: 'Not applicable'
};

export function statusTone(kind: StatusKind, status: string | null | undefined): StatusTone {
  return (status && TONES[kind][status]) || 'plain';
}

export function statusBadgeClass(kind: StatusKind, status: string | null | undefined): string {
  return `lb-pill lb-pill--${statusTone(kind, status)}`;
}

export function statusLabel(status: string | null | undefined): string {
  if (!status) return '';
  return LABELS[status] || status;
}

/** Every status a kind can show, with its tone (the style guide lists these). */
export function statusesOf(kind: StatusKind): { status: string; tone: StatusTone }[] {
  return Object.keys(TONES[kind]).map(status => ({ status, tone: TONES[kind][status] }));
}

export const STATUS_KINDS: StatusKind[] = ['order', 'shipment', 'purchaseOrder', 'purchaseRequest', 'stockCount', 'productionOrder', 'kpi', 'damage', 'billing'];
